import { Injectable } from '@angular/core';
import { CompanyType, Steps, Sections, PermissionType } from '../constants';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';
import { checkEmailFormat } from '../validators';

@Injectable({
  providedIn: 'root',
})
export class CompaniesService {
  loading = false;
  currentSection = Sections.Info;
  currentStep = Steps.INFO;
  screen = Steps.INFO;
  currentTab = Steps.INFO;
  isBeingCreated = new BehaviorSubject<boolean>(false);

  private readonly backToMainEvent = new BehaviorSubject<boolean>(false);
  backToMainEvent$ = this.backToMainEvent.asObservable();

  private readonly refetchCompanies = new BehaviorSubject<boolean>(false);
  refetchCompanies$ = this.refetchCompanies.asObservable();

  private companiesSubject = new BehaviorSubject<CompanyType[]>([]);
  companies$ = this.companiesSubject.asObservable();
  currentCompany: CompanyType;
  companyLogoErrorState = false;

  resetEditFields;

  // Users Enable for Create/Edit Companies
  rolesEnableForCreate = [PermissionType.Admin];
  rolesEnableForEdit = [PermissionType.Admin, PermissionType.Distributor, PermissionType.Company];

  constructor(private readonly authService: AuthService) {
    this.isBeingCreated.next(false);
  }

  setCompanies(newCompanies: CompanyType[]) {
    this.companiesSubject.next(newCompanies);
  }

  setCurrentTab(tab: Steps) {
    this.currentTab = tab;
  }

  getPermissionTypes() {
    // Roles enable for edit user's permissions
    switch (this.authService.user.role) {
      case PermissionType.Admin:
        return [PermissionType.Distributor, PermissionType.Company, PermissionType.End_Company, PermissionType.Viewer];
      case PermissionType.Distributor:
        return [PermissionType.Company, PermissionType.End_Company, PermissionType.Viewer];
      case PermissionType.Company:
      default:
        return [PermissionType.End_Company, PermissionType.Viewer];
    }
  }

  enableEditPermission(user, company, ignoreLength = false) {
    const availablePermission = this.getPermissionTypes();
    return (
      (availablePermission.length > 1 || ignoreLength) &&
      availablePermission.includes(user.role) &&
      company.email != user.email
    );
  }

  checkEnableFunction(array, role) {
    return array.includes(role);
  }

  onReset = () => {
    this.currentSection = Sections.Info;
    this.resetEditScreen();
  };

  goToInfo = () => {
    this.currentSection = Sections.Info;
    this.resetEditScreen();
  };

  removeCompany(companyID) {
    this.setCompanies(this.companiesSubject.value.filter((c) => c._id != companyID));
  }

  updateCompanyInList(updatedCompany: CompanyType) {
    const index = this.companiesSubject.value.findIndex((c) => c._id === updatedCompany._id);
    if (index === -1) return;

    const companies = [...this.companiesSubject.value];
    companies[index] = updatedCompany;
    this.setCompanies(companies);
  }

  getCompany(companyID) {
    return this.companiesSubject.value.find((c) => c._id === companyID);
  }

  resetEditScreen = () => {
    this.currentStep = Steps.INFO;
    this.screen = Steps.INFO;
  };

  minimumRequirementsMet() {
    const hasName = !!this.currentCompany.name;
    const isCreating = this.isBeingCreated.getValue();
    if (isCreating) {
      return hasName && checkEmailFormat(this.currentCompany.email);
    }
    return hasName && (!this.currentCompany.email || checkEmailFormat(this.currentCompany.email));
  }

  onEdit = (company: CompanyType, isKaironEnabled: boolean) => {
    this.isBeingCreated.next(false);
    this.currentCompany = {
      ...JSON.parse(JSON.stringify(company)),
      language: company.language ?? this.authService.selectedLanguage,
      isKaironEnabled,
    };

    if (this.currentTab == Steps.USERS && this.minimumRequirementsMet()) {
      this.currentStep = Steps.USERS;
      this.screen = Steps.USERS;
    } else if (this.currentTab == Steps.SETTINGS) {
      this.currentStep = Steps.SETTINGS;
      this.screen = Steps.SETTINGS;
    } else {
      this.resetEditScreen();
    }

    this.currentSection = Sections.Edit;
  };

  onCreate = () => {
    this.currentCompany = new CompanyType();
    this.isBeingCreated.next(true);
    this.currentCompany.language = this.authService.selectedLanguage;
    if (this.resetEditFields) {
      this.resetEditFields();
    }
    this.currentSection = Sections.Create;
    this.resetEditScreen();
  };

  onBack = () => {
    this.currentSection = Sections.Info;
  };

  setBackToMainEvent(value: boolean): void {
    this.backToMainEvent.next(value);
  }

  setRefetchCompanies(value: boolean): void {
    this.refetchCompanies.next(value);
  }

  handleImageError() {
    this.companyLogoErrorState = true;
  }

  clear() {
    this.setCompanies([]);
    this.currentCompany = undefined;
  }
}
